import { useState, useCallback } from "react";

/**
 * Manages pagination state and provides a helper to slice any array
 * to the current page's items.
 *
 * @param totalItems - Total number of items to paginate
 * @param pageSize   - Number of items per page (default 20)
 *
 * Requirements: 8.2, 8.3
 */
export function usePagination(
  totalItems: number,
  pageSize: number = 20
): {
  currentPage: number;
  totalPages: number;
  setPage: (page: number) => void;
  resetPage: () => void;
  paginatedItems: <T>(items: T[]) => T[];
} {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(totalItems / pageSize);

  const setPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const paginatedItems = useCallback(
    <T>(items: T[]): T[] => {
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize;
      return items.slice(start, end);
    },
    [currentPage, pageSize]
  );

  return { currentPage, totalPages, setPage, resetPage, paginatedItems };
}
